import { FxIdApplicationStoreCreatePaymentHandlerEmbeddingType, FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "@fxid/openapi-client";
import OpenApiClient from "@fxid/openapi-client";
import { EFxIdSdkAdapterInfoSocialCapability, EFxIdSdkGender } from "@fxid/sdk-core";
import { AppConfig, getAuthToken, joinURL } from "@fxid/shared";
import { deferred } from "../types/FxIdSdkUtils";
import { FxIdSdkBaseAdapter } from "./FxIdSdkBaseAdapter";
export class FxIdSdkAdapterForWizQ extends FxIdSdkBaseAdapter {
    constructor(exportedSdk, game, config, dependencies) {
        super(exportedSdk, game, config, dependencies);
        this.exportedSdk = exportedSdk;
        this.game = game;
        this.config = config;
        this.adapter = "ymobage";
        this._wizQRequestNum = 0;
    }
    RegisterShareHandlers() {
        return Promise.resolve();
    }
    async BuyProduct(request) {
        const paymentRequest = {
            Token: getAuthToken(),
            Game: this.game,
            Sku: request.sku,
            WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ,
            EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
            ProductDescriptionHint: request.productDescriptionHint,
            ProductNameHint: request.productNameHint
        };
        if (request.developerPayload != null) {
            paymentRequest.DeveloperPayload = {
                MerchantDeveloperPayload: request.developerPayload
            };
        }
        const createPaymentResult = await OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest);
        log.info("Received result from server: %o", createPaymentResult);
        const { promise, resolve, reject } = deferred();
        // Fxid-ордер создаётся в webhook updateInventory, а не здесь.
        // SetOptimisticOrderStatus не вызываем — ордер не существует до момента колбэка от wizq.
        this.buyProductResolve = (data) => {
            log.info("[WIZQ] Payment completed, skipping optimistic status (order created in webhook)");
            resolve({
                ...data,
                transactionId: createPaymentResult.TransactionId,
                stats: {
                    currency: createPaymentResult.OrderProduct.Currency ?? "",
                    currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
                    usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
                }
            });
        };
        this.buyProductReject = (data) => {
            log.info("[WIZQ] Payment rejected, skipping optimistic status (order created in webhook)");
            reject(data);
        };
        // Единый grant-on-webhook path: SkuId всегда заполнен (wizqSkuId или XOR(product.id)).
        // Fxid-ордер создаётся в webhook updateInventory при успешной оплате.
        const wizQData = createPaymentResult.WizQ;
        log.info("[WIZQ] Grant-on-webhook: skuId=%o itemName=%o skuUnitPrice=%o", wizQData.SkuId, wizQData.ItemName, wizQData.SkuUnitPrice);
        wqapi.requestPayment(wizQData.SkuId.toString(), 1, wizQData.SkuUnitPrice, wizQData.ItemName, wizQData.SkuImageURL, wizqConfig.handlePaymentURL, (returnCode, returnMessage) => {
            log.info("wqapi.requestPayment: returnCode: %o, returnMessage: %o", returnCode, returnMessage);
            if (returnCode === WizQAPI.ReturnCode.SUCCESS.code) {
                this.buyProductResolve?.call(this, {});
            }
            else {
                this.buyProductReject?.call(this, {});
            }
        });
        return promise;
    }
    async GetUserIdToken() {
        const getUserIdCommandName = "getUserId";
        const userCommandReply = await this.MakeProxyRequest({
            command: getUserIdCommandName,
            num: this._wizQRequestNum++
        });
        // Запросы два раза заэнкожены
        const reply = JSON.parse(JSON.parse(userCommandReply));
        return reply.Token;
    }
    async MakeProxyRequest(command) {
        // Т.к. wizq шлет по сути данные формы, то отдаем ему джейсон в application/x-www-form-urlencoded
        const params = new URLSearchParams();
        params.append("json", JSON.stringify(command));
        const data = params.toString();
        const url = joinURL(AppConfig.apiBasePath, `/api/v1/wizq/proxyRequestHandler/${this.game}`);
        log.warn("Making proxy request with command %o. Encoded data: %s. Url: %s", command, data, url);
        return await new Promise((resolve, reject) => {
            wqapi.makeRequest(url, "POST", data, (returnCode, returnMessage, response) => {
                log.info("Received proxy response to command %o with code %s, message %s, response %s", command, returnCode, returnMessage, response);
                if (returnCode === WizQAPI.ReturnCode.SUCCESS.code) {
                    //success
                    resolve(response);
                }
                else {
                    reject();
                }
            });
        });
    }
    async GetSocialInfo() {
        const userId = await this.WizQGetUserId();
        const userInfo = (await this.WizQGetUsersInfo([userId]))[userId];
        const gender = this.MapWizQGender(userInfo.gender);
        return Promise.resolve({
            social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ,
            paymentsAvailable: true,
            userId: userId,
            displayName: userInfo.displayName,
            photo: userInfo.picUrl ? { url: userInfo.picUrl } : undefined,
            gender,
            socialLocale: "ja", // wizq разрешает только японский
            capabilities: [
                EFxIdSdkAdapterInfoSocialCapability.LegalDialogsDisabled,
                EFxIdSdkAdapterInfoSocialCapability.SupportDialogsDisabled,
                EFxIdSdkAdapterInfoSocialCapability.ExternalIncentivesDisabled
            ]
        });
    }
    async Initialize() {
        log.info("Initializing WizQ adapter");
        // Load the SDK's source Asynchronously
        await new Promise((resolve, reject) => {
            const wizqBootStrap = new WizQBootStrap();
            wizqBootStrap.init(wizqConfig);
            wizqBootStrap.registerOnLoadHandler(() => {
                // resetHeight убран — DOMContentLoaded хак уже вызывает gadgets.window.adjustHeight,
                // а resetHeight переводит iframe в auto-resize режим, что вызывает скачки размера
                wqapi.getSelfID((code, message, userId) => {
                    if (code !== WizQAPI.ReturnCode.SUCCESS.code || !userId) {
                        return reject({ code, message });
                    }
                    this._userId = userId;
                    return resolve();
                });
            });
            log.info("Script added");
        });
        log.info("Initializing api");
    }
    async RegisterIframeWindow() {
        try {
            log.info("Registering iframe Window in WizQ: %o", this.config.PlatformData.WizQ);
            // resetHeight — официальный WizQ API для подгонки высоты canvas под приложение.
            // Вызываем здесь (после рендера game container), чтобы WizQ измерил
            // правильную высоту контента и учёл cross-sell баннеры.
            wqapi.resetHeight();
        }
        catch (e) {
            log.error(e);
        }
    }
    StoreCurrency() {
        return Promise.resolve(undefined);
    }
    async StatInitialize(_request) {
        return Promise.resolve();
    }
    StatEvent(_request) {
        return Promise.resolve(undefined);
    }
    async GetFriends(_request) {
        const friendsData = await this.WizQGetFriendsInfo();
        const result = { friends: [] };
        for (const friendData of Object.values(friendsData)) {
            const gender = this.MapWizQGender(friendData.gender);
            const friend = {
                uid: friendData.uid,
                displayName: friendData.displayName,
                gender,
                photo: friendData.picUrl ? { url: friendData.picUrl } : undefined
            };
            result.friends.push(friend);
        }
        return result;
    }
    async WordsFilter(request) {
        const wizQResult = await this.WizQValidateUserInput(request.text);
        return { success: wizQResult.result };
    }
    WizQGetUserId() {
        return new Promise((resolve, reject) => {
            if (this._userId)
                return resolve(this._userId);
            wqapi.getSelfID((code, message, userId) => {
                if (code !== WizQAPI.ReturnCode.SUCCESS.code || !userId)
                    return reject({ code, message });
                this._userId = userId;
                return resolve(userId);
            });
        });
    }
    WizQGetFriendsInfo(hasApp = false) {
        return new Promise((resolve, reject) => {
            wqapi.getFriendsID(hasApp, (code, message, friendIds) => {
                if (code !== WizQAPI.ReturnCode.SUCCESS.code || !friendIds)
                    return reject({ code, message });
                this.WizQGetUsersInfo(friendIds)
                    .then((friendsData) => {
                    resolve(friendsData);
                })
                    .catch((reason) => {
                    reject(reason);
                });
            });
        });
    }
    WizQValidateUserInput(input) {
        return new Promise((resolve, reject) => {
            wqapi.validateUserInput(input, (code, message, result) => {
                if (code !== WizQAPI.ReturnCode.SUCCESS.code)
                    return reject({ code, message });
                resolve({ result: result });
            });
        });
    }
    WizQGetUsersInfo(userIds) {
        return new Promise((resolve, reject) => {
            wqapi.getUsersProfile(userIds, [
                WizQAPI.User.Fields.FIELD_ID,
                WizQAPI.User.Fields.FIELD_DISPLAY_NAME,
                WizQAPI.User.Fields.FIELD_GENDER,
                WizQAPI.User.Fields.FIELD_PROFILE_IMAGE_URL
            ], (code, message, data) => {
                const res = {};
                if (code !== WizQAPI.ReturnCode.SUCCESS.code || !data)
                    return reject({ code, message });
                for (const userInfo of Object.values(data)) {
                    res[userInfo[WizQAPI.User.Fields.FIELD_ID]] = {
                        uid: userInfo[WizQAPI.User.Fields.FIELD_ID],
                        displayName: userInfo[WizQAPI.User.Fields.FIELD_DISPLAY_NAME],
                        gender: userInfo[WizQAPI.User.Fields.FIELD_GENDER],
                        picUrl: userInfo[WizQAPI.User.Fields.FIELD_PROFILE_IMAGE_URL]
                    };
                }
                resolve(res);
            });
        });
    }
    MapWizQGender(gender) {
        switch (gender) {
            case 1:
                return EFxIdSdkGender.Male;
            case 2:
                return EFxIdSdkGender.Female;
            default:
                return EFxIdSdkGender.Unspecified;
        }
    }
}
